import { useEffect, useRef, useState } from "react";
import { ChromaFlow, FilmGrain, Glass, Shader, Swirl } from "shaders/react";

export interface PriceShaderProps {
  children: string;
  className?: string;
}

// Three.js's NodeMaterial / TSL emits GLSL3 uniform names with a mangled
// `__` suffix (e.g. `baseColor__r_3_`). Per GLSL ES 3.00 §3.7, identifiers
// containing two consecutive underscores are reserved — ANGLE (Chromium)
// and WebKit reject the program. Three then keeps calling useProgram on
// the dead program every frame, flooding the GPU with INVALID_OPERATION
// until iOS Safari kills the tab as "a problem repeatedly occurred."
//
// When a real WebGPU adapter is available, Three picks the WebGPU / WGSL
// pipeline instead — WGSL has no reserved-identifier rule, so the shader
// compiles cleanly. That's the path current Chrome / Edge / Safari desktop
// take. We verify the adapter *actually* resolves (not just that
// `navigator.gpu` is exposed) because some Safari builds advertise WebGPU
// but `requestAdapter()` returns null — in that state Three falls back to
// WebGL and triggers the iOS crash loop again.
let shaderSupportedPromise: Promise<boolean> | null = null;
function detectShaderSupport(): Promise<boolean> {
  if (shaderSupportedPromise) return shaderSupportedPromise;
  if (typeof document === "undefined") return Promise.resolve(false);
  shaderSupportedPromise = (async () => {
    if (
      typeof navigator !== "undefined" &&
      "gpu" in navigator &&
      navigator.gpu
    ) {
      try {
        const adapter = await navigator.gpu.requestAdapter();
        if (adapter) return true;
      } catch {
        // requestAdapter threw — fall through to the sync GLSL probe.
      }
    }
    try {
      const probe = document.createElement("canvas");
      const gl = probe.getContext("webgl2");
      if (!gl) return false;
      const fs = gl.createShader(gl.FRAGMENT_SHADER);
      if (!fs) return false;
      gl.shaderSource(
        fs,
        `#version 300 es
precision mediump float;
uniform float test__var;
out vec4 outColor;
void main() { outColor = vec4(test__var); }`,
      );
      gl.compileShader(fs);
      const ok = gl.getShaderParameter(fs, gl.COMPILE_STATUS) === true;
      gl.deleteShader(fs);
      return ok;
    } catch {
      return false;
    }
  })();
  return shaderSupportedPromise;
}

export function PriceShader({ children, className }: PriceShaderProps) {
  // Start in fallback (safe default). The adapter probe is async, so we
  // can't synchronously promise the shader will work — and on iOS Safari
  // a premature mount is what triggers the crash loop. Once the probe
  // resolves we either flip to shader mode or stay in fallback.
  const [fallback, setFallback] = useState(true);
  // Defer mounting the WebGL context until the price band scrolls into
  // view. The shader animates every frame while alive, so keeping it off
  // until needed saves battery for everyone — and on browsers that did
  // pass the support probe but only just barely, the context isn't even
  // created if the visitor bounces before reaching the pricing section.
  const [visible, setVisible] = useState(false);
  const wrapRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = async () => {
      if (mq.matches) {
        if (!cancelled) setFallback(true);
        return;
      }
      const supported = await detectShaderSupport();
      if (!cancelled) setFallback(!supported);
    };
    update();
    mq.addEventListener("change", update);
    return () => {
      cancelled = true;
      mq.removeEventListener("change", update);
    };
  }, []);

  useEffect(() => {
    if (fallback) return;
    const node = wrapRef.current;
    if (!node) return;
    if (!("IntersectionObserver" in window)) {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setVisible(true);
            io.disconnect();
            return;
          }
        }
      },
      { rootMargin: "200px" },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [fallback]);

  if (fallback) {
    return <span className={className}>{children}</span>;
  }

  return (
    <span ref={wrapRef} className={`${className ?? ""} has-shader`.trim()}>
      <span className="sr-only">{children}</span>
      <span className="price-shader-canvas" aria-hidden="true">
        {visible && (
          <Shader
            disableTelemetry
            style={{ width: "100%", height: "100%", display: "block" }}
          >
            <Glass
              aberration={1}
              blur={20}
              cutout={true}
              fresnel={0.02}
              fresnelSoftness={0.31}
              highlight={0.3}
              refraction={1.57}
              scale={{
                type: "map",
                source: "",
                channel: "alpha",
                inputMin: 0,
                inputMax: 1,
                outputMin: 0.1,
                outputMax: 3,
              }}
              shapeSdfUrl="https://data.shaders.com/storage/v1/object/public/user-uploaded-images/user_3DeZbqaSaJYXbOpP4XtFri4E2ao/UBNGo77EIBkE_sdf.bin"
              thickness={1}
              transform={{ scale: 2.02 }}
            >
              <Swirl
                blend={18}
                colorA="#141412"
                colorB="#ffffff"
                colorSpace="hsl"
                detail={4.2}
                speed={0.5}
              />
              <ChromaFlow />
            </Glass>
            <FilmGrain strength={0.05} visible={false} />
          </Shader>
        )}
      </span>
    </span>
  );
}
